// @ts-ignore
import Plot from 'react-plotly.js';
import { IconCircuitGround } from "@tabler/icons-react";
import { CardActions } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState, useRef, memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import LegendToggleRoundedIcon from '@mui/icons-material/LegendToggleRounded';
import theme from "../../../../assets/theme";
import KZTypeSelector from "../../../../components/selectors/KZTypeSelector";
import NodeSelector from "../../../../components/selectors/NodeSelector";
import { useApiCall } from "../../../../hooks/useApiCall";
import useComponentSize from "../../../../hooks/useComponentSize";
import i18nStore from "../../../../i18n/store";
import { NodeInterface } from "../../../tables/schemas/nodes";
import { SCCCalcApi } from "../../api";
import { CalcNodeInterface, ChartInterface } from "../../schemas";
import { GraphAll } from "../../utils";
import { getNodeChartData, getNodeChartLayout, getSIZE_RANGE, getVectors } from "./utils";

interface VectorChartProps {
    nodes: NodeInterface[];
    calcNodes: CalcNodeInterface[];
    chart: ChartInterface;
    onDuplicateChart: (project_id: number, chart_id: number) => void;
}
const VectorChart = (props: VectorChartProps) => {
    const translations = i18nStore.getTranslations;

    const [row, setRow] = useState<CalcNodeInterface | undefined>(undefined);
    const [showLegend, setShowLegend] = useState<boolean>(false);
    const [showV0, setShowV0] = useState<boolean>(true);

    const ref = useRef<HTMLInputElement>(null);
    const sizes = useComponentSize(ref);

    const [scType, setScType] = useState<number>(props.chart.sc_type);
    const [selectedDamageLocation, setSelectedDamageLocation] = useState<NodeInterface | null>(null);
    const [selectedMeasurementLocation, setSelectedMeasurementLocation] = useState<NodeInterface | null>(null);

    const deleteNodeChartApi = useApiCall(() => SCCCalcApi.deleteChart(props.chart.project_id, props.chart.id), null);

    useEffect(() => {
        if (props.nodes.length > 0 && props.chart.damage_location && props.chart.measure_location) {
            setSelectedDamageLocation(props.nodes.find(node => node.id === props.chart.damage_location) || null);
            setSelectedMeasurementLocation(props.nodes.find(node => node.id === props.chart.measure_location) || null);
        }
    }, [props.nodes, props.chart.damage_location, props.chart.measure_location]);

    useEffect(() => {
        if (props.calcNodes.length > 0 && selectedMeasurementLocation !== null && selectedDamageLocation !== null) {
            const newRow: CalcNodeInterface | undefined = props.calcNodes.find((calcNode: CalcNodeInterface) => {
                return calcNode.sc_type === scType
                    && calcNode.node_pk === selectedMeasurementLocation?.id
                    && calcNode.sc_node_pk === selectedDamageLocation?.id;
            });
            setRow(newRow);
        }
    }, [props.calcNodes, scType, selectedMeasurementLocation, selectedDamageLocation]);

    const toggleLegend = () => {
        if (selectedMeasurementLocation && selectedDamageLocation) {
            setShowLegend(!showLegend);
        }
    }

    const onDeleteChart = () => {
        deleteNodeChartApi.apiCaller()?.then(() => {
            props.chart.deleted = true;
        });
    };
    const onUpdateChart = (newChartData: object) => {
        SCCCalcApi.updateChart(
            props.chart.project_id,
            props.chart.id,
            newChartData,
        ).then(() => {
            props.chart.sc_type = scType;
            props.chart.measure_location = selectedMeasurementLocation?.id;
            props.chart.damage_location = selectedDamageLocation?.id;
        });
    };

    const vectors: (GraphAll | null) = useMemo(() => getVectors(row, showV0), [row, showV0]);

    // Create the quiver plot
    const chartVectors: object[] = useMemo(() => getNodeChartData(vectors, translations), [vectors, translations]);

    // Layout configuration
    const SIZE_RANGE = useMemo(() => getSIZE_RANGE(row), [row]);
    const layout = useMemo(
        () => getNodeChartLayout(sizes.width - 50, 380, showLegend, SIZE_RANGE),
        [showLegend, SIZE_RANGE, sizes.width]
    );


    if (props.chart.deleted) return null;

    return (
        <div ref={ref}>
            <Card sx={{ mb: 1, }}>
                <CardContent sx={{
                    "& .modebar": { left: 0 },
                }}>
                    <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <KZTypeSelector
                            kzType={scType}
                            setKzType={(v) => {
                                setScType(v);
                                onUpdateChart(
                                        {
                                            ...props.chart,
                                            sc_type: v,
                                            damage_location: selectedDamageLocation?.id,
                                            measure_location: selectedMeasurementLocation?.id,
                                        }
                                );
                            }}
                            id={`labelKZSelector2`}
                        />
                        <Box>
                            <Tooltip title={showV0 ? translations._charts.hideV0 : translations._charts.showV0}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowV0(!showV0)}
                                        disabled={selectedMeasurementLocation === null || selectedDamageLocation === null}
                                        sx={{
                                            ml: 1,
                                            background: showV0 ? theme.palette.primary.main : "none",
                                            color: showV0 ? "white" : theme.palette.primary.main,
                                            "&:hover": {
                                                background: theme.palette.primary.dark,
                                                color: "white",
                                            },
                                            fontSize: "1.5rem",
                                        }}
                                    >
                                        <IconCircuitGround />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title={translations._charts.toggleLegend}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={toggleLegend}
                                        disabled={selectedMeasurementLocation === null || selectedDamageLocation === null}
                                        sx={{
                                            ml: 1,
                                            background: showLegend ? theme.palette.primary.main : "none",
                                            color: showLegend ? "white" : theme.palette.primary.main,
                                            "&:hover": {
                                                background: theme.palette.primary.dark,
                                                color: "white",
                                            },
                                        }}
                                    >
                                    <LegendToggleRoundedIcon />
                                </IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", my: 2 }}>
                        <NodeSelector
                            inputValue={selectedDamageLocation}
                            setInputValue={(v) => {
                                setSelectedDamageLocation(v);
                                onUpdateChart(
                                    {
                                        ...props.chart,
                                        scType: scType,
                                        damage_location: v ? v.id : null,
                                        measure_location: selectedMeasurementLocation?.id,
                                    }
                                );
                            }}
                            nodes={props.nodes}
                            label={translations.damageLocation}
                        />
                        <NodeSelector
                            inputValue={selectedMeasurementLocation}
                            setInputValue={(v) => {
                                setSelectedMeasurementLocation(v);
                                onUpdateChart(
                                    {
                                        ...props.chart,
                                        scType: scType,
                                        measure_location: v ? v.id : null,
                                        damage_location: selectedDamageLocation?.id,
                                    }
                                );
                            }}
                            nodes={props.nodes}
                            label={translations.measurementPlace}
                        />
                    </Box>

                    <Chart data={ Boolean(row) ? chartVectors : null } layout={layout} />

                </CardContent>
                <CardActions sx={{ justifyContent: "space-between" }}>
                    <Button
                        size="small"
                        color="primary"
                        startIcon={<ControlPointDuplicateIcon />}
                        variant="contained"
                        onClick={() => props.onDuplicateChart(props.chart.project_id, props.chart.id)}
                    >
                        {translations.duplicate}
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        variant="contained"
                        onClick={() => onDeleteChart()}
                        loading={deleteNodeChartApi.loading}
                    >
                        {translations.delete}
                    </Button>
                </CardActions>
            </Card>
        </div>

    );
};

export default VectorChart;


interface ChartProps {
    data: object[] | null;
    layout: object;
}
const Chart = memo(function (props: ChartProps) {

    const handleLegendClick = (event: any) => {
        // console.log("handleLegendClick", event);
    };
    const handleHover = (event: any) => {
        // console.log("handleHover", event);
    };

    return (
        <Plot
            data={props.data}
            layout={props.layout}
            config={{
                displayModeBar: true,  // This hides the toolbar
                displaylogo: false,    // Hide Plotly logo if desired
                // This is the key configuration for positioning
                modeBar: {
                    orientation: 'h', // horizontal orientation
                    bgcolor: 'rgba(255, 255, 255, 0.8)', // background color
                    color: '#000', // icon color
                    activecolor: '#4477AA', // active icon color
                    xanchor: 'left', // anchor x position
                    yanchor: 'top', // anchor y position
                    x: 0, // x position (0 = left)
                    y: 1.1 // y position (1.1 = just above the chart)
                }
            }}
            onLegendClick={handleLegendClick}
            onHover={handleHover}
        />
    );
});